/**
 * COP-134 D1 / ATRU-04 — the pure render half of the read-only admin session
 * viewer.
 *
 * WHY ITS OWN MODULE. `Show.tsx` imports `@/lib/hub-client`, which constructs a
 * `HubClient` at module load and throws `HubClient: baseUrl not provided` in the
 * no-jsdom unit environment. Everything the tests assert lives here, so
 * `show.test.tsx` can render it with `renderToStaticMarkup` without booting a
 * client (same reason `schedules/run-history-row.tsx` and
 * `schedules/created-in-chat-badge.tsx` were extracted).
 *
 * WHY IT REUSES THE COPILOT HELPERS. `convertToUIMessages`, `groupParts` and
 * `CopilotTurnActions` are the SAME exports the Copilot widget renders with. A
 * second parts-to-render implementation here would drift from the widget's.
 *
 * COP-135 D1/D6 (TACT-06) — the per-turn `N action(s)` count this surface used
 * to render is GONE, together with `summarizeTurnActivity` /
 * `formatTurnActivity`. The turn's actions arrive NAMED, with their outcomes, in
 * the message's own `data-turn-actions` part, written by the hub from the
 * `tf_turn_action` ledger. This viewer renders those rows verbatim and derives
 * nothing — an admin surface that computed its own opinion of what a turn did
 * would be the third producer COP-135 rejects.
 *
 * READ-ONLY (hard constraint). This surface renders what happened; it never
 * acts. No approve/reject, no message input, no delete, no retry — interactive
 * approval belongs to the Copilot, where the tool-policy layer runs (COP-016,
 * COP-044).
 */
import type {
  TfChatMessageRow,
  TfChatSessionRow,
} from '@verybigthings/trackframe-shared/schemas';
import {
  convertToUIMessages,
  CopilotTurnActions,
  groupParts,
  routeTurnActions,
  type GroupedPart,
} from '@verybigthings/trackframe-hub-client/ui/components/primitives';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert } from '@/components/ui/alert';
import { fmtIso } from '@/lib/format';

/** The `GET /sessions/:id/messages` payload (`SessionMessagesResponseSchema`). */
export interface SessionMessagesData {
  session: TfChatSessionRow;
  messages: TfChatMessageRow[];
}

export const SESSION_LOADING_COPY = 'Loading…';
export const SESSION_DENIED_COPY = 'You do not have access to this session';
export const SESSION_NOT_FOUND_COPY = 'Session not found';
export const SESSION_EMPTY_COPY = 'This session has no messages.';

export type SessionViewerState =
  | { kind: 'loading' }
  | { kind: 'error'; httpStatus: number | null; message: string }
  | { kind: 'ready'; data: SessionMessagesData };

/**
 * Hub status → user-facing copy. The hub answers `GET /sessions/:id/messages`
 * with 403 when the `session#read = owner ∪ parent.track#admin` guard denies
 * the caller, and 404 when the row does not exist. Any other failure keeps the
 * client's own message rather than guessing.
 */
export function sessionErrorCopy(httpStatus: number | null, message: string): string {
  if (httpStatus === 403) return SESSION_DENIED_COPY;
  if (httpStatus === 404) return SESSION_NOT_FOUND_COPY;
  return message;
}

/**
 * COP-131 marker: a scheduled run stamps `{ origin, scheduleId, scheduledRunId }`
 * onto `tf_chat_session.metadata`. PRESENT or ABSENT, never `null` — an absent
 * marker is what every interactive session holds.
 */
export function scheduledRunIdOf(session: TfChatSessionRow): string | undefined {
  const value = (session.metadata as Record<string, unknown> | null)?.scheduledRunId;
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/** One compact labelled row per non-text group: what ran, and how it ended. */
function groupRows(group: GroupedPart): Array<{ label: string; status: string }> {
  switch (group.type) {
    case 'text':
      return [];
    case 'tools':
      return group.tools.map((tool) => ({
        label: tool.toolName,
        status: tool.state ?? 'output-available',
      }));
    case 'memory-suggestion':
      return [{ label: 'Memory suggestion', status: group.suggestion.state }];
    case 'doc-share-suggestion':
      return [{ label: 'Document share', status: group.suggestion.state }];
    case 'forget-memory':
      return [{ label: 'Forget memory', status: group.suggestion.state }];
    case 'approval':
      // ATRU-01: `execution` is the truth about whether the approved call ran.
      return [{
        label: `Approval — ${group.approval.toolName}`,
        status: group.approval.execution ?? group.approval.state,
      }];
    case 'job':
      // COP-156 D5: a long-running track job, at the status the hub hydrated.
      return [{ label: `Job — ${group.job.toolName}`, status: group.job.status }];
    case 'plan':
      return [{ label: 'Plan', status: group.initialState ?? 'pending' }];
    case 'skill-loaded':
      return [{ label: `Skill — ${group.skill.name}`, status: 'loaded' }];
    case 'db-table':
      return [{ label: 'DB query result', status: 'rendered' }];
    case 'schedule-preview':
      return [{ label: 'Schedule', status: group.terminalState }];
  }
}

function SessionHeader({ session }: { session: TfChatSessionRow }) {
  const scheduledRunId = scheduledRunIdOf(session);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <span>Session</span>
          <code className="font-mono text-xs" data-testid="session-id">{session.id}</code>
          {scheduledRunId ? (
            <Badge data-testid="session-scheduled-badge" title={scheduledRunId}>
              Scheduled
            </Badge>
          ) : null}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm md:grid-cols-2">
          <div className="flex justify-between gap-2 border-b pb-1">
            <dt className="text-muted-foreground">Track</dt>
            <dd className="font-mono text-xs">{session.trackId ?? '—'}</dd>
          </div>
          <div className="flex justify-between gap-2 border-b pb-1">
            <dt className="text-muted-foreground">Title</dt>
            <dd className="text-xs">{session.title ?? '—'}</dd>
          </div>
          <div className="flex justify-between gap-2 border-b pb-1">
            <dt className="text-muted-foreground">Created</dt>
            <dd className="font-mono text-xs">{fmtIso(session.createdAt)}</dd>
          </div>
          <div className="flex justify-between gap-2 border-b pb-1">
            <dt className="text-muted-foreground">Updated</dt>
            <dd className="font-mono text-xs">{fmtIso(session.updatedAt)}</dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
}

function SessionMessages({ messages }: { messages: TfChatMessageRow[] }) {
  // The SAME conversion the Copilot performs on reload: the server-hydrated
  // `uiParts` win when present, older rows fall back to content + toolResults.
  const uiMessages = convertToUIMessages(messages);
  // COP-135 D1 (task-07 AC15) — the same routing the Copilot uses, from the same
  // export. A reloaded transcript gives every message exactly one part built
  // from its own `turn_id`, so the routing is a no-op here; sharing it is what
  // keeps this viewer from becoming a second opinion about which message owns
  // which turn.
  const turnActionsByMessage = routeTurnActions(uiMessages);

  if (uiMessages.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="session-empty">
        {SESSION_EMPTY_COPY}
      </p>
    );
  }

  return (
    <ol className="space-y-3">
      {uiMessages.map((message, index) => {
        const rawParts = ((message as { parts?: unknown }).parts ?? []) as Parameters<
          typeof groupParts
        >[0];
        const groups = groupParts(rawParts, { role: message.role });

        return (
          <li
            key={message.id}
            className="rounded-md border bg-background px-3 py-2"
            data-role={message.role}
          >
            <div className="text-xs font-semibold uppercase text-muted-foreground">
              {message.role}
            </div>
            {groups.map((group, gi) =>
              group.type === 'text' ? (
                <p key={gi} className="whitespace-pre-wrap text-sm">{group.text}</p>
              ) : (
                groupRows(group).map((row, ri) => (
                  <div
                    key={`${gi}-${ri}`}
                    className="mt-1 flex items-center gap-2 text-xs"
                    data-transcript-group={group.type}
                  >
                    <code className="font-mono">{row.label}</code>
                    <span className="text-muted-foreground">{row.status}</span>
                  </div>
                ))
              ),
            )}
            {/* COP-135 D1 — the turn's actions, named, with their outcomes.
                Routed on the server's `turnId`; a message that carries none
                (a session older than the ledger) shows no rows. */}
            <CopilotTurnActions
              snapshots={turnActionsByMessage[index] ?? []}
              className="mt-2"
            />
          </li>
        );
      })}
    </ol>
  );
}

/**
 * The read-only viewer body. Four distinct states: loading, denied (403),
 * not found (404), and ready — where a session with zero messages renders the
 * empty state rather than a blank page.
 */
export function SessionViewer({ state }: { state: SessionViewerState }) {
  if (state.kind === 'loading') {
    return (
      <Card>
        <CardContent className="py-4 text-sm text-muted-foreground">
          {SESSION_LOADING_COPY}
        </CardContent>
      </Card>
    );
  }

  if (state.kind === 'error') {
    return (
      <Card>
        <CardContent className="py-4">
          <Alert>{sessionErrorCopy(state.httpStatus, state.message)}</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <SessionHeader session={state.data.session} />
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Transcript</CardTitle>
        </CardHeader>
        <CardContent>
          <SessionMessages messages={state.data.messages} />
        </CardContent>
      </Card>
    </div>
  );
}
